<template>
  <div>
    <div v-for="([orgId, config]) in Object.entries(orgUserTagFormConfigByOrgId)">
      <v-select v-for="q in config.questions"
                :key="`userTagForm_${orgId}_${q.value}`"
                v-model="formObject[orgId][q.value]"
                :items="q.answers.map(e => ({ title: e.label, value: e.value }))"
                :label="q.label + (q.required ? ' (required)' : '')"
                :rules="q.required ? [requiredRule] : []"
                variant="outlined"
                density="compact"
                class="mb-2"
                :readonly="q.readOnly"
                :clearable="!q.readOnly"
      ></v-select>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { requiredRule } from '@/service/uiUtils.js'
import { useKinectinStore } from '@/store/kinectin.js'
import { storeToRefs } from 'pinia'
import { every } from 'lodash-es'

const emit = defineEmits(['update:modelValue'])

const props = defineProps({
  modelValue: { type: Object },
  formConfig: { type: Object },
  orgId: { type: String },
})

const store = useKinectinStore()
const { followedOrgs } = storeToRefs(store)

const formObject = ref({})
const orgUserTagFormConfigByOrgId = ref({})

onMounted(async () => {
  if (props.formConfig) {
    orgUserTagFormConfigByOrgId.value[props.orgId] = props.formConfig
    syncFormObject()
    return
  }
  await store.loadFollowedOrgs()
  followedOrgs.value.map(e => orgUserTagFormConfigByOrgId.value[e.orgId] = e.userTagFormConfig || {})
  syncFormObject()
})
watch(() => props.formConfig, () => {
  orgUserTagFormConfigByOrgId.value[props.orgId] = props.formConfig
  syncFormObject()
})

function syncFormObject() {
  // initialize the formObject to have an empty map for all orgIds
  Object.keys(orgUserTagFormConfigByOrgId.value)
      .map(orgId => formObject.value[orgId] = formObject.value[orgId] || {})

  // set the values for valid orgId tag keys
  for (const e of props.modelValue || []) {
    const [orgId, k, v] = e.split(':')
    if (formObject.value[orgId] && k) formObject.value[orgId][k] = v
  }

  applyConfiguredDefaults()
}

function applyConfiguredDefaults() {
  for (const [orgId, config] of Object.entries(orgUserTagFormConfigByOrgId.value)) {
    for (const q of config?.questions || []) {
      if (q.readOnly && q.defaultAnswerValue) {
        formObject.value[orgId][q.value] = q.defaultAnswerValue
      } else if (!formObject.value[orgId][q.value] && q.defaultAnswerValue) {
        formObject.value[orgId][q.value] = q.defaultAnswerValue
      }
    }
  }
}

watch(formObject, () => {
  applyConfiguredDefaults()

  // respect existing tags, but replace orgId tag key/value pairs
  const configuredOrgIds = Object.keys(orgUserTagFormConfigByOrgId.value)
  const newValue = (props.modelValue || [])
      .filter(tag => every(configuredOrgIds, orgId => !tag.startsWith(`${orgId}:`)))

  for (const [orgId, config] of Object.entries(orgUserTagFormConfigByOrgId.value)) {
    for (const q of config?.questions || []) {
      const answer = formObject.value[orgId][q.value]
      if (answer) newValue.push(`${orgId}:${q.value}:${answer}`)
    }
  }
  emit('update:modelValue', newValue)
}, { deep: true })

</script>

<style>
</style>
